import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";

function pickMime() {
  const list = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  for (const m of list) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

/**
 * Voice recorder hook for the Translator.
 *
 * `onBlob(blob)` is called once a recording finishes (length > 1500 bytes).
 * Returns `{ recording, toggleRecording, isBusy }` where `isBusy` reflects
 * the recorder lifecycle independent of the parent's "AI is translating"
 * state (which the parent passes back in as `disabled`).
 *
 * Behaviour matches the pre-refactor inline implementation exactly:
 *  - first attempt grabs `getUserMedia({ audio: true })`
 *  - records into a `MediaRecorder` with the best supported mime
 *  - on stop, tracks are released and the blob is forwarded to `onBlob`
 *  - very short (accidental) clips < 1500 bytes are silently dropped
 */
export function useVoiceRecorder({ onBlob, disabled }) {
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingRef = useRef(false);
  const startingRef = useRef(false);

  const startRecording = useCallback(async () => {
    if (disabled || recordingRef.current || startingRef.current) return;
    startingRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: pickMime() });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, {
          type: mr.mimeType || "audio/webm",
        });
        stream.getTracks().forEach((t) => t.stop());
        recorderRef.current = null;
        recordingRef.current = false;
        setRecording(false);
        if (blob.size < 1500) {
          toast.message("Recording was too short — try again");
          return;
        }
        onBlob(blob);
      };
      recorderRef.current = mr;
      mr.start();
      recordingRef.current = true;
      setRecording(true);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("getUserMedia failed:", err?.message || err);
      }
      toast.error("Microphone permission denied");
    } finally {
      startingRef.current = false;
    }
  }, [disabled, onBlob]);

  const stopRecording = useCallback(() => {
    const mr = recorderRef.current;
    if (mr && recordingRef.current) {
      try {
        mr.stop();
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("recorder.stop failed:", err?.message || err);
        }
      }
    }
  }, []);

  const toggleRecording = useCallback(() => {
    if (disabled) return;
    if (recordingRef.current) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [disabled, startRecording, stopRecording]);

  return { recording, toggleRecording };
}
